import { useState } from 'react';
import { ProfileSection } from '@/components/organisms/ProfileSection';
import { SubscribersByRegionChart } from '@/components/organisms/SubscribersByRegionChart';
import { ChurnRateChart } from '@/components/organisms/ChurnRateChart';
import { RankBars } from '@/components/organisms/RankBars';
import { SegmentedControl } from '@/components/organisms/SegmentedControl';
import { PlatformIcon, SplitBar } from '@/components/primitives';
import { StatTile } from '@/components/organisms/StatTile';
import { colorForRegion } from '@/lib/titleCharts';
import { formatArpu, formatCount, formatPct, prettyQuarter } from '@/lib/format';
import { formatSignedCount } from '@/components/organisms/QuarterlyPerformanceTable';
import { usePlatformProfileContext } from './PlatformProfileLayout';
import { PROFILE_REGIONS, useRegionHistories } from './usePlatformProfile';
import styles from './ProfileTabs.module.css';

type PeerMetric = 'subscribers' | 'net_additions' | 'churn_rate';
const PEER_OPTS = [
  { value: 'subscribers', label: 'Subscribers' },
  { value: 'net_additions', label: 'Net Additions' },
  { value: 'churn_rate', label: 'Churn Rate' },
];
const PEER_FORMAT: Record<PeerMetric, (v: number) => string> = {
  subscribers: formatCount,
  net_additions: formatSignedCount,
  churn_rate: (v) => formatPct(v),
};

/** Subscribers — by region over time, churn, regions now, against peers. */
export function SubscribersTab() {
  const { id, region, setRegion, profile } = usePlatformProfileContext();
  const { history, historyAll, regions, leaderboard } = profile;
  const { byRegion } = useRegionHistories(id);
  const [peerMetric, setPeerMetric] = useState<PeerMetric>('subscribers');

  if (historyAll.isLoading) {
    return (
      <div className={styles.tab}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderLabel}>Loading subscriber data…</span>
        </div>
      </div>
    );
  }
  if (!history.length) {
    return (
      <div className={styles.tab}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderLabel}>No subscriber history available</span>
        </div>
      </div>
    );
  }

  const regionScope = region ?? 'All regions';
  const latestQ = prettyQuarter(history[history.length - 1].quarter);
  const span = `${prettyQuarter(history[0].quarter)} – ${latestQ}`;
  const qs = region ? `?region=${region}` : '';

  const board = leaderboard.data ?? [];
  const peerAverage = board.length ? board.reduce((a, r) => a + r.churn_rate, 0) / board.length : undefined;

  // Churn ranks ascending (lowest churn is best), the rest descending.
  const peers = [...board].sort((a, b) =>
    peerMetric === 'churn_rate' ? a.churn_rate - b.churn_rate : b[peerMetric] - a[peerMetric],
  );

  const regionRows = [...(regions.data ?? [])].sort((a, b) => b.subscribers - a.subscribers);
  const regionTotal = regionRows.reduce((a, r) => a + r.subscribers, 0) || 1;

  return (
    <div className={styles.tab}>
      <ProfileSection
        title="Subscribers by Region"
        scope={`${regionScope} · ${span}`}
        hint="Subscribers at the end of each quarter, stacked by region."
      >
        <SubscribersByRegionChart platform={id} byRegion={byRegion} regions={region ? [region] : [...PROFILE_REGIONS]} />
      </ProfileSection>

      <ProfileSection
        title="Churn Rate"
        scope={`${regionScope} · ${span}`}
        hint="Share of subscribers lost in the quarter. The dashed line is the average churn of every platform with streaming metrics, latest quarter, in the same region."
      >
        <ChurnRateChart platform={id} history={history} peerAverage={peerAverage} peerCount={board.length} />
      </ProfileSection>

      <ProfileSection
        title="Regions Now"
        scope={latestQ}
        hint="Each region's subscribers, share of the platform's total, new subscribers and ARPU in the latest quarter. Pick a region to scope the whole profile to it; pick it again to clear."
        boxed={false}
      >
        <div className={styles.regionTiles}>
          {regionRows.map((r) => {
            const selected = r.region === region;
            return (
              <StatTile
                key={r.region}
                label={r.region}
                value={formatCount(r.subscribers)}
                sub={`${formatPct((r.subscribers / regionTotal) * 100, 0)} of subscribers`}
                accent={colorForRegion(r.region)}
                facts={[
                  { label: 'Gross adds', value: formatCount(r.gross_additions) },
                  { label: 'ARPU', value: formatArpu(r.arpu) },
                ]}
                selected={selected}
                onToggle={() => setRegion(selected ? '' : r.region)}
              >
                <SplitBar extent={r.subscribers / regionTotal} segments={[{ fraction: 1, color: colorForRegion(r.region) }]} />
              </StatTile>
            );
          })}
        </div>
      </ProfileSection>

      <ProfileSection
        title="Against Other Platforms"
        scope={`${regionScope} · ${latestQ}`}
        hint="Every platform with streaming metrics, in the same region and quarter. For churn, lower is better, so #1 has the lowest churn."
        controls={
          <SegmentedControl
            variant="field"
            options={PEER_OPTS}
            value={peerMetric}
            onChange={(v) => setPeerMetric(v as PeerMetric)}
          />
        }
      >
        <RankBars
          items={peers.map((p) => ({
            key: p.platform,
            label: p.platform,
            icon: <PlatformIcon name={p.platform} size={20} />,
            value: p[peerMetric],
            display: PEER_FORMAT[peerMetric](p[peerMetric]),
            highlight: p.platform === id,
            href: `/platforms/${encodeURIComponent(p.platform)}/subscribers${qs}`,
          }))}
        />
      </ProfileSection>
    </div>
  );
}
